import React, { useEffect, useState } from 'react';
import { LeafIcon, MenuIcon, XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type PageType = 'home' | 'about' | 'services' | 'industries' | 'projects' | 'team' | 'contact';
interface NavbarProps {
  currentPage: PageType;
  onNavigate: (page: PageType) => void;
}
const navLinks: {
  name: string;
  page: PageType;
}[] = [{
  name: 'About',
  page: 'about'
}, {
  name: 'Services',
  page: 'services'
}, {
  name: 'Industries',
  page: 'industries'
}, {
  name: 'Projects',
  page: 'projects'
}, {
  name: 'Team',
  page: 'team'
}, {
  name: 'Contact',
  page: 'contact'
}];
export function Navbar({
  currentPage,
  onNavigate
}: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isHome = currentPage === 'home';
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleNavClick = (page: PageType) => {
    onNavigate(page);
    setIsMobileMenuOpen(false);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const showSolidBg = isScrolled || !isHome;
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${showSolidBg ? 'bg-white shadow-sm py-4' : 'bg-transparent py-6'}`} data-id="element-195">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-196">
        <div className="flex justify-between items-center" data-id="element-197">
          {/* Logo */}
          <button onClick={() => handleNavClick('home')} className="flex items-center gap-2.5 group" data-id="element-198">
            <img src="https://cdn.magicpatterns.com/uploads/uNzxRhCv2aCUAsyC2zwLfQ/Greenwater_logo.jpg" alt="Greenwater Logo" className="h-9 w-9 rounded-full object-cover ring-2 ring-white/20 group-hover:ring-gesl-gold/40 transition-all duration-300" data-id="element-199" />
            <span className="text-xl tracking-wide transition-colors duration-300" data-id="element-200">
              <span className={`font-extrabold ${showSolidBg ? 'text-gesl-green' : 'text-gesl-gold'}`} data-id="element-201">
                GREEN
              </span>
              <span className={`font-light ${showSolidBg ? 'text-gesl-dark' : 'text-white'}`} data-id="element-202">
                WATER
              </span>
            </span>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8" data-id="element-203">
            {navLinks.map(link => <button key={link.name} onClick={() => handleNavClick(link.page)} className={`text-sm font-medium transition-colors hover:text-gesl-gold ${showSolidBg ? 'text-gesl-charcoal' : 'text-white/90'} ${currentPage === link.page ? 'text-gesl-gold' : ''}`} data-id="element-204">
                {link.name}
              </button>)}
            <button onClick={() => handleNavClick('contact')} className={`px-5 py-2.5 rounded-sm text-sm font-semibold transition-all ${showSolidBg ? 'bg-gesl-dark text-white hover:bg-gesl-green' : 'bg-gesl-gold text-white hover:bg-white hover:text-gesl-dark'}`} data-id="element-205">
              Get in Touch
            </button>
          </nav>

          {/* Mobile Menu Toggle */}
          <button className="md:hidden p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" data-id="element-206">
            {isMobileMenuOpen ? <XIcon className={`h-6 w-6 ${showSolidBg ? 'text-gesl-dark' : 'text-white'}`} data-id="element-207" /> : <MenuIcon className={`h-6 w-6 ${showSolidBg ? 'text-gesl-dark' : 'text-white'}`} data-id="element-208" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence data-id="element-209">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden bg-white border-t border-gray-100 overflow-hidden" data-id="element-210">
            <div className="px-4 py-6 space-y-4 flex flex-col" data-id="element-211">
              {navLinks.map(link => <button key={link.name} onClick={() => handleNavClick(link.page)} className={`text-left text-gesl-charcoal font-medium text-lg py-2 border-b border-gray-50 ${currentPage === link.page ? 'text-gesl-gold' : ''}`} data-id="element-212">
                  {link.name}
                </button>)}
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}